import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, Search, Check, X } from 'lucide-react';
import {
  fetchClientsByStatus,
  approveClient,
  rejectClient
} from '../../../services/admin/api';
import './client-list.css';

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [isAsc, setIsAsc] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const location = useLocation();

  const isPending = location.pathname.includes('pending');
  const isRejected = location.pathname.includes('rejected');
  const isApproved = !isPending && !isRejected;

  const status = isPending
    ? 'PENDING'
    : isRejected
      ? 'REJECTED'
      : 'APPROVED';




  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);



        const result = await fetchClientsByStatus(
          status,
          currentPage,
          itemsPerPage,
          sortColumn,
          isAsc,
          searchTerm
        );

        if (result?.content) {
          setClients(result.content);
          setTotalPages(result.totalPages);
          setTotalElements(result.totalElements);
          setPageSize(result.size);

        } else {
          setClients([]);
          setTotalPages(0);
        }
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [status, currentPage, itemsPerPage, sortColumn, isAsc, searchTerm]);


  const renderSortArrow = (field) => {
    const isActive = sortColumn === field;

    return (
      <span
        className={`sort-icons ${isActive ? "sort-active" : ""
          }`}
      >

        {/* showing the next action button  */}
        {!isActive
          ? "▲▼"
          : sortOrder === "asc"
            ? "▼" : "▲"
        }
      </span>
    );
  };


  // handle the sort with consistency icons and defualt sort asc . 
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Same column -> toggle
      const newIsAsc = !isAsc;
      setIsAsc(newIsAsc);
      setSortOrder(newIsAsc ? "asc" : "desc");
    } else {
      // Different column -> default to ascending
      setSortColumn(column);
      setIsAsc(true);
      setSortOrder("asc");
    }

    setCurrentPage(0);
  };


  const paginatedClients = clients;


  // ---------------- ACTIONS ----------------

  const handleNavigateToCandidates = (client) => {
    navigate(`/admin/client/${client.email}/candidates`, {
      state: {
        heading: 'Candidates List',
        clientUserId: client.id,
        clientName: client.username,
        companyName: client.companyName,
      },
    });
  };

  const handleApprove = async (id) => {
    await approveClient(id);
    // optional: refetch instead of filter
    setClients(prev => prev.filter(c => c.id !== id));
  };

  const handleReject = async (id) => {
    await rejectClient(id);
    setClients(prev => prev.filter(c => c.id !== id));
  };

  // ---------------- UI ----------------

  return (
    <div className="client-wrapper">
      <div className="clinet-table-controls">
        <div className="entries-select">
          Show{" "}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>{" "}
          entries
        </div>

        <div className="client-search-box">
          <div className="client-search-input-wrapper">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(0);
              }}
            />
            <Search className="client-search-icon" size={18} />
          </div>
        </div>
      </div>

      <div className="clinet-table-container">
        <table className="candidates-clinet-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('username')}>
                Client Name {renderSortArrow('username')}
              </th>

              <th onClick={() => handleSort('companyname')}>
                Company {renderSortArrow('companyname')}
              </th>

              <th onClick={() => handleSort('email')}>
                Email {renderSortArrow('email')}
              </th>

              <th onClick={() => handleSort('phonenumber')}>
                Contact Number {renderSortArrow('phonenumber')}
              </th>

              {!isRejected && <th>Actions</th>}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isRejected ? "4" : "5"} className="no-data loading-text">
                  Loading Clients...
                </td>
              </tr>
            ) : paginatedClients.length > 0 ? (
              paginatedClients.map((client) => (
                <tr key={client.id}>
                  <td>{client.username}</td>
                  <td>{client.companyName}</td>
                  <td>{client.email}</td>
                  <td>{client.phoneNumber}</td>

                  {!isRejected && (
                    <td>
                      {isPending && (
                        <>
                          <button
                            className="candidate-btn"
                            title="Approve"
                            onClick={() => handleApprove(client.id)}
                          >
                            <Check size={16} />
                          </button>

                          <button
                            className="candidate-btn"
                            title="Reject"
                            onClick={() => handleReject(client.id)}
                          >
                            <X size={16} />
                          </button>
                        </>
                      )}

                      {isApproved && (
                        <button
                          className="candidate-btn"
                          title="View Candidates"
                          onClick={() => handleNavigateToCandidates(client)}
                        >
                          <Users size={18} />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  No matching clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ Pagination (Backend Controlled) */}
      <div className="pagination">
        <div className="pagination-summary">
          {totalElements === 0 ? (
            <>Showing 0 to 0 of 0 entries</>
          ) : (
            <>
              Showing {currentPage * pageSize + 1} to{" "}
              {currentPage * pageSize + clients.length} of{" "}
              {totalElements} entries
            </>
          )}
        </div>

        <div className="pagination-buttons-user">
          <button
            className="page-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
            disabled={currentPage === 0}
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              className={`page-btn ${currentPage === index ? "active" : ""}`}
              onClick={() => setCurrentPage(index)}
            >
              {index + 1}
            </button>
          ))}

          <button
            className="page-btn"
            onClick={() =>
              setCurrentPage(prev => Math.min(prev + 1, totalPages - 1))
            }
            disabled={currentPage >= totalPages - 1 || totalPages === 0}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientList;